import { Level, parseChargingTime } from '@/lib/chargingTime';
import { ChargingInfoType } from '@/lib/schema/inventory/types';
import clsx from 'clsx';
import { FC, useMemo } from 'react';
import { VDPChargingCardWrapper } from '../VDPChargingCardWrapper/VDPChargingCardWrapper';

interface VDPChargingTimeCardProps {
  chargingInfo: ChargingInfoType;
  fueltype: string;
  infoCircle?: boolean;
}

const getLevelMinutes = (level: Level | null) => {
  if (!level) return 0;
  return level.unit === 'hours'
    ? Number(level.value) * 60
    : Number(level.value);
};
const getWidthPercentage = (level: Level | null, maxMinutes: number) => {
  if (!level) return 0;
  return level.unit === 'hours'
    ? (Number(level.value) * 60) / maxMinutes
    : Number(level.value) / maxMinutes;
};

const LevelBar = ({
  level,
  time,
  width,
}: {
  level: '1' | '2' | '3';
  time: string;
  width: number;
}) => {
  return (
    <div className="flex items-center gap-s">
      <div className="whitespace-nowrap text-microLight text-neutral-800">
        Level {level}
      </div>
      <div
        className={clsx('flex h-s rounded-full', {
          'bg-neutral-300': time === 'N/A',
          'bg-[#BBE6A3]': time !== 'N/A' && level === '1',
          'bg-[#90E95F]': time !== 'N/A' && level === '2',
          'bg-[#479C19]': time !== 'N/A' && level === '3',
        })}
        style={{
          width: `${width}%`,
        }}
      />
      <div className="whitespace-nowrap text-microMedium text-neutral-800">
        {time}
      </div>
    </div>
  );
};

export const VDPChargingTimeCard: FC<VDPChargingTimeCardProps> = ({
  chargingInfo,
  fueltype,
  infoCircle,
}) => {
  const { level1, level2, level3, fastChargeTime } = useMemo(() => {
    const level1Times = chargingInfo.level1
      ? parseChargingTime(chargingInfo.level1)
      : null;
    const level2Times = chargingInfo.level2
      ? parseChargingTime(chargingInfo.level2)
      : null;
    const level3Times = chargingInfo.level3
      ? parseChargingTime(chargingInfo.level3)
      : null;

    const lvl1Mins = getLevelMinutes(level1Times);
    const lvl2Mins = getLevelMinutes(level2Times);
    const lvl3Mins = getLevelMinutes(level3Times);
    const maxTime = Math.max(lvl1Mins, lvl2Mins, lvl3Mins);

    const fastChargeTimeRes = Math.min(
      lvl1Mins || Infinity,
      lvl2Mins || Infinity,
      lvl3Mins || Infinity
    );
    return {
      level1: level1Times
        ? { ...level1Times, width: getWidthPercentage(level1Times, maxTime) }
        : null,
      level2: level2Times
        ? { ...level2Times, width: getWidthPercentage(level2Times, maxTime) }
        : null,
      level3: level3Times
        ? { ...level3Times, width: getWidthPercentage(level3Times, maxTime) }
        : null,
      fastChargeTime: fastChargeTimeRes,
    };
  }, [chargingInfo]);

  let chargingValue = 'N/A';
  if (fastChargeTime !== Infinity) {
    chargingValue =
      fastChargeTime > 120
        ? `${(fastChargeTime / 60).toLocaleString()} hours`
        : `${fastChargeTime.toLocaleString()} mins`;
  }

  return (
    <VDPChargingCardWrapper
      title={'Charging time'}
      infoCircle={infoCircle}
      value={chargingValue}
      subtitle={`How fast this ${fueltype === 'Electric' ? 'EV' : 'PHEV'} can charge.`}
      content={
        <div className="flex flex-col gap-xs">
          <LevelBar
            level="1"
            time={level1 ? `${level1.value} ${level1.unit}` : 'N/A'}
            width={level1 ? Math.max(5, level1.width * 100) : 100}
          />

          <LevelBar
            level="2"
            time={level2 ? `${level2.value} ${level2.unit}` : 'N/A'}
            width={level2 ? Math.max(5, level2.width * 100) : 100}
          />
          {fueltype === 'Electric' && (
            <LevelBar
              level="3"
              time={level3 ? `${level3.value} ${level3.unit}` : 'N/A'}
              width={level3 ? Math.max(5, level3.width * 100) : 100}
            />
          )}
        </div>
      }
    />
  );
};
